import React from 'react';
import './styles/PlanDetailsPanel.css';
import { translations } from './dictionary/translatePlanDetails';

function PlanDetailsPanel({ planDetails, usedStorage, dailyUploads, selectedLanguage, outputLanguage, translatePage }) {
    const { planType = 'N/A', limits = {} } = planDetails || {};
    
    // Função utilitária para verificar se o valor é "ilimitado"
    const isUnlimited = (value) => value === Number.MAX_SAFE_INTEGER || value.toString().includes('Unlimited');

    const totalStorage = isUnlimited(limits?.storage)
        ? Number.MAX_SAFE_INTEGER
        : parseFloat(limits?.storage?.replace('GB', '')) || 0;
    const dailyLimit = isUnlimited(limits?.dailyLimit) ? Number.MAX_SAFE_INTEGER : limits?.dailyLimit || 0;
    const maxFileSize = isUnlimited(limits?.maxFileSize)
        ? Number.MAX_SAFE_INTEGER
        : parseFloat(limits?.maxFileSize?.replace('GB', '')) || 0;

    // Função utilitária para formatar o armazenamento com unidade dinâmica
    const formatStorage = (valueInGB) => {
        if (isUnlimited(valueInGB)) return translations[selectedLanguage]?.['unlimited'] || 'Unlimited';

        const useDecimalBase = process.env.REACT_APP_USE_DECIMAL_BASE === 'true';
        const base = useDecimalBase ? 1000 : 1024;
        const positiveValue = Math.max(0, valueInGB); // Evita valores negativos

        if (positiveValue >= base) {
            const valueInTB = positiveValue / base;
            return `${valueInTB.toFixed(2)}TB`;
        }
        if (positiveValue < 1) {
            const valueInMB = positiveValue * base;
            return `${valueInMB.toFixed(2)}MB`;
        }
        return `${positiveValue.toFixed(2)}GB`;
    };

    // Cálculo correto da porcentagem de uploads realizados
    const storagePercentage = totalStorage && totalStorage !== Number.MAX_SAFE_INTEGER
        ? (usedStorage / totalStorage) * 100
        : 0;
    const uploadsPercentage = dailyLimit && dailyLimit !== Number.MAX_SAFE_INTEGER
        ? (dailyUploads / dailyLimit) * 100
        : 0;

    // Determinar idioma ativo
    const activeLanguage = translatePage ? outputLanguage : selectedLanguage;

    // Função para obter a tradução com base no idioma ativo
    const t = (key) => translations[activeLanguage]?.[key] || key;

    return (
        <div className="plan-details-container">
            <h4>{t('planDetailsHeader')}</h4>
            <p>{`${t('planType')}: ${planType || t('unavailable')}`}</p>

            {/* Espaço usado */}
            <p>
                {`${t('storageUsed')}: ${
                    isUnlimited(totalStorage) ? t('unlimited') : formatStorage(usedStorage)
                } ${t('of')} ${formatStorage(totalStorage)}`}
            </p>
            <div className="plan-progress-bar">
                <div
                    className="plan-progress-bar-fill"
                    style={{ width: `${storagePercentage}%` }}
                >
                    <span className="progress-bar-text">
                        {isUnlimited(totalStorage) ? t('unlimited') : `${Math.round(storagePercentage)}%`}
                    </span>
                </div>
            </div>

            {/* Uploads restantes */}
            <p>
                {`${t('uploadsRemaining')}: ${
                    isUnlimited(dailyLimit) ? t('unlimited') : `${dailyUploads} ${t('of')} ${dailyLimit}`
                }`}
            </p>
            <div className="plan-progress-bar">
                <div
                    className="plan-progress-bar-fill"
                    style={{ width: `${uploadsPercentage}%` }}
                >
                    <span className="progress-bar-text">
                        {isUnlimited(dailyLimit) ? t('unlimited') : `${Math.round(uploadsPercentage)}%`}
                    </span>
                </div>
            </div>

            {/* Tamanho máximo de arquivo */}
            <p>{`${t('maxFileSize')}: ${formatStorage(maxFileSize) || t('unlimited')}`}</p>
        </div>
    );
}

export default PlanDetailsPanel;
